import { useState, useCallback, useMemo } from "react";
import { useScenario } from "../../state";
import * as Fmt from "../../format";
import { Section } from "../../components";
import {
  RENT_PCT_REVENUE_CAUTION,
  RENT_PCT_REVENUE_THRESHOLD,
} from "../../model";
import { Histogram, DEFAULT_MC_VARIABLES } from "./components";
import { useMonteCarloWorker } from "../../workers/hooks";
import { GEARED_REFERENCE_DISCLOSURE } from "../../decision-metrics";
import { monteCarloRunFingerprint } from "../../monte-carlo-fingerprint";
import {
  DSCR_STRUCTURAL_BREACH_LABEL,
  OPERATOR_EBITDA_NEGATIVE_Y1_BREACH_LABEL,
  operatorEbitdaNegativePostRampBreachLabel,
  GEARED_REFERENCE_LABEL,
  developerGearedReferenceHeroFootnote,
} from "../../presentation/return-labels";

export { operatorEbitdaNegativePostRampBreachLabel as postRampEbitdaBreachLabel } from "../../presentation/return-labels";

export function RiskAnalysis() {
  const { active, mcResult, mcHash, setMcResult } = useScenario();
  const [vars, setVars] = useState(() =>
    DEFAULT_MC_VARIABLES.map((v) => ({ ...v, enabled: true })),
  );
  const [iterations, setIterations] = useState(2000);
  const { run: runMc, running, progress, error } = useMonteCarloWorker();
  const runDefinition = useMemo(() => {
    const enabled = vars.filter((variable) => variable.enabled);
    const ranges: Record<string, number> = {};
    for (const variable of enabled) {
      if (
        variable.range !==
        DEFAULT_MC_VARIABLES.find((candidate) => candidate.key === variable.key)
          ?.range
      ) {
        ranges[variable.key] = variable.range;
      }
    }
    return {
      enabledKeys: enabled.map((variable) => variable.key),
      ranges: Object.keys(ranges).length > 0 ? ranges : undefined,
      iterations,
    };
  }, [iterations, vars]);
  const currentMcHash = monteCarloRunFingerprint(active, runDefinition);
  const mcStale = mcResult !== null && mcHash !== currentMcHash;

  const run = useCallback(() => {
    runMc(
      active,
      runDefinition.enabledKeys,
      runDefinition.ranges,
      runDefinition.iterations,
      (res) =>
        setMcResult(res, monteCarloRunFingerprint(active, runDefinition)),
    );
  }, [active, runDefinition, setMcResult, runMc]);

  const toggleVar = (key: string) => {
    setVars((prev) =>
      prev.map((v) => (v.key === key ? { ...v, enabled: !v.enabled } : v)),
    );
  };

  const changeRange = (key: string, range: number) => {
    setVars((prev) => prev.map((v) => (v.key === key ? { ...v, range } : v)));
  };

  const a = active.assumptions;
  const exitYear = a.developerExitYear ?? a.modelYears;
  const gearedDistributionLabel =
    exitYear < a.modelYears
      ? `Developer IRR (geared, project Y${exitYear} early exit) distribution`
      : "Developer IRR (geared, sale at completion) distribution";

  return (
    <div className="page fade-in">
      <Section
        eyebrow="Risk Analysis"
        title="Monte Carlo simulation"
        lede={`Run ${iterations.toLocaleString("en-AU")} stochastic iterations to estimate the probability distribution of key outcomes. Select variables and ranges below, then run.`}
      >
        <div className="grid g-1-2" style={{ gap: 24 }}>
          {/* Variable selection */}
          <div className="card elevated card-pad-lg">
            <div className="eyebrow" style={{ marginBottom: 12 }}>
              Variables & ranges
            </div>
            <div className="col" style={{ gap: 10 }}>
              {vars.map((v) => (
                <div
                  key={v.key}
                  className="row between"
                  style={{ gap: 8, alignItems: "center" }}
                >
                  <label
                    className="row gap-2"
                    style={{ cursor: "pointer", flex: 1 }}
                  >
                    <input
                      type="checkbox"
                      checked={v.enabled}
                      onChange={() => toggleVar(v.key)}
                    />
                    <span className="p-small">{v.label}</span>
                  </label>
                  {v.enabled && (
                    <div className="row gap-1" style={{ alignItems: "center" }}>
                      <span className="p-micro">±</span>
                      <input
                        type="number"
                        aria-label={`${v.label} range percentage`}
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(v.range * 100)}
                        onChange={(e) => {
                          const n = parseFloat(e.target.value);
                          if (Number.isFinite(n)) changeRange(v.key, n / 100);
                        }}
                        style={{
                          width: 50,
                          padding: "4px 6px",
                          fontSize: 12,
                          borderRadius: 6,
                          border: "1px solid hsl(var(--c-border))",
                          background: "hsl(var(--c-background))",
                          color: "hsl(var(--c-foreground))",
                        }}
                      />
                      <span className="p-micro">%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: "1px solid hsl(var(--c-muted))",
              }}
            >
              <div className="row between" style={{ marginBottom: 8 }}>
                <span className="p-small">
                  Iterations{" "}
                  <span
                    className="p-micro"
                    style={{ color: "hsl(var(--c-foreground-muted))" }}
                  >
                    (paths)
                  </span>
                </span>
                <select
                  aria-label="Monte Carlo iterations"
                  value={iterations}
                  onChange={(e) => setIterations(Number(e.target.value))}
                  style={{
                    padding: "4px 8px",
                    fontSize: 13,
                    borderRadius: 6,
                    border: "1px solid hsl(var(--c-border))",
                    background: "hsl(var(--c-background))",
                    color: "hsl(var(--c-foreground))",
                  }}
                >
                  <option value={500}>500</option>
                  <option value={1000}>1,000</option>
                  <option value={2000}>2,000</option>
                  <option value={5000}>5,000</option>
                </select>
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={run}
                disabled={running}
                aria-busy={running}
                title={
                  running
                    ? "Simulation in progress…"
                    : "Run Monte Carlo simulation"
                }
                style={{ width: "100%", opacity: running ? 0.7 : 1 }}
              >
                {running ? "Running simulation…" : "Run Monte Carlo"}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="col" style={{ gap: 16 }}>
            {error && !running && (
              <div
                className="banner danger"
                role="alert"
                style={{ display: "block" }}
              >
                <strong>Simulation failed.</strong> Review the scenario inputs
                and retry. If the problem repeats, record the scenario details
                for support.
                <div className="p-small" style={{ marginTop: 6 }}>
                  {error}
                </div>
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={run}
                    aria-label="Retry Monte Carlo"
                  >
                    Retry Monte Carlo
                  </button>
                </div>
              </div>
            )}

            {mcStale && !running && (
              <div className="banner warn" role="status">
                <strong>Results are stale — re-run required.</strong> Scenario
                assumptions, buildings, or simulation controls have changed
                since this simulation. The prior results remain visible for
                context only.
              </div>
            )}

            {!mcResult && !running && !error && (
              <div
                className="card elevated card-pad-lg"
                style={{
                  textAlign: "center",
                  color: "hsl(var(--c-foreground-muted))",
                }}
              >
                <div className="p-lead">No simulation run yet</div>
                <div className="p-small">
                  Select variables and click <strong>Run Monte Carlo</strong> to
                  see risk threshold probabilities and outcome distributions.
                </div>
              </div>
            )}

            {running && (
              <div
                className="card elevated card-pad-lg"
                style={{ textAlign: "center" }}
              >
                <div className="p-lead">
                  Running {iterations.toLocaleString("en-AU")} iterations…
                </div>
                <div
                  style={{
                    margin: "12px auto 0",
                    width: "80%",
                    height: 8,
                    borderRadius: 4,
                    background: "hsl(var(--c-border))",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${(progress / iterations) * 100}%`,
                      height: "100%",
                      borderRadius: 4,
                      background: "hsl(var(--c-primary))",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <div
                  className="p-small"
                  style={{
                    color: "hsl(var(--c-foreground-muted))",
                    marginTop: 6,
                  }}
                >
                  {Math.round((progress / iterations) * 100)}%
                </div>
              </div>
            )}

            {mcResult && (
              <>
                {/* Financing-reference probability */}
                <div className="card elevated card-pad-lg">
                  <div className="eyebrow" style={{ marginBottom: 8 }}>
                    {GEARED_REFERENCE_LABEL}
                  </div>
                  {Number.isFinite(mcResult.hurdleMetPct) &&
                  Number.isFinite(mcResult.hurdleReferenceIrr) ? (
                    <div
                      className="row gap-3"
                      style={{ alignItems: "baseline" }}
                    >
                      <div
                        className="stat-display"
                        style={{
                          fontSize: 36,
                          color: "hsl(var(--c-foreground))",
                        }}
                      >
                        {Fmt.pct(mcResult.hurdleMetPct, 0)}
                      </div>
                      <span className="p-small">
                        chance matched-horizon geared IRR ≥{" "}
                        {Fmt.pct(mcResult.hurdleReferenceIrr!, 0)} reference
                      </span>
                    </div>
                  ) : (
                    <div className="p-lead">
                      {developerGearedReferenceHeroFootnote(null)}
                    </div>
                  )}
                  <div className="p-small" style={{ marginTop: 8 }}>
                    {GEARED_REFERENCE_DISCLOSURE}
                  </div>
                </div>

                <div className="grid g-2">
                  {/* Developer IRR distribution */}
                  <div className="card elevated card-pad-lg">
                    <div className="eyebrow" style={{ marginBottom: 8 }}>
                      {gearedDistributionLabel}
                    </div>
                    <Histogram
                      counts={mcResult.developerGearedIrr.histogram}
                      color="hsl(140 60% 45%)"
                    />
                    <div className="grid g-4" style={{ marginTop: 12 }}>
                      <div>
                        <div className="p-micro">Mean</div>
                        <div className="mono">
                          {Fmt.pct(mcResult.developerGearedIrr.mean, 1)}
                        </div>
                      </div>
                      <div>
                        <div className="p-micro">P5</div>
                        <div
                          className="mono"
                          style={{ color: "hsl(var(--c-destructive))" }}
                        >
                          {Fmt.pct(mcResult.developerGearedIrr.p5, 1)}
                        </div>
                      </div>
                      <div>
                        <div className="p-micro">P50</div>
                        <div className="mono">
                          {Fmt.pct(mcResult.developerGearedIrr.p50, 1)}
                        </div>
                      </div>
                      <div>
                        <div className="p-micro">P95</div>
                        <div
                          className="mono"
                          style={{ color: "hsl(var(--c-success))" }}
                        >
                          {Fmt.pct(mcResult.developerGearedIrr.p95, 1)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Operator NPV distribution */}
                  <div className="card elevated card-pad-lg">
                    <div className="eyebrow" style={{ marginBottom: 8 }}>
                      {a.operatorName} NPV distribution
                    </div>
                    <Histogram
                      counts={mcResult.reddamNpv.histogram}
                      color="hsl(210 60% 55%)"
                    />
                    <div className="grid g-4" style={{ marginTop: 12 }}>
                      <div>
                        <div className="p-micro">Mean</div>
                        <div className="mono">
                          {Fmt.compactCurrency(mcResult.reddamNpv.mean)}
                        </div>
                      </div>
                      <div>
                        <div className="p-micro">P5</div>
                        <div
                          className="mono"
                          style={{ color: "hsl(var(--c-destructive))" }}
                        >
                          {Fmt.compactCurrency(mcResult.reddamNpv.p5)}
                        </div>
                      </div>
                      <div>
                        <div className="p-micro">P50</div>
                        <div className="mono">
                          {Fmt.compactCurrency(mcResult.reddamNpv.p50)}
                        </div>
                      </div>
                      <div>
                        <div className="p-micro">P95</div>
                        <div
                          className="mono"
                          style={{ color: "hsl(var(--c-success))" }}
                        >
                          {Fmt.compactCurrency(mcResult.reddamNpv.p95)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk threshold probabilities */}
                <div className="card elevated card-pad-lg">
                  <div className="eyebrow" style={{ marginBottom: 12 }}>
                    Risk threshold probabilities
                  </div>
                  <div className="col" style={{ gap: 10 }}>
                    {[
                      {
                        label: `Rent > caution band (${Fmt.pct(RENT_PCT_REVENUE_CAUTION, 0)} of revenue)`,
                        pct: mcResult.breach.rentAboveCautionPct,
                      },
                      {
                        label: `Rent > hard ceiling (${Fmt.pct(RENT_PCT_REVENUE_THRESHOLD, 0)} of revenue)`,
                        pct: mcResult.breach.rentAboveThresholdPct,
                      },
                      {
                        label: OPERATOR_EBITDA_NEGATIVE_Y1_BREACH_LABEL,
                        pct: mcResult.breach.ebitdaNegativeY1Pct,
                      },
                      {
                        label: operatorEbitdaNegativePostRampBreachLabel(
                          a.rampYears,
                        ),
                        pct: mcResult.breach.ebitdaNegativePostRampPct,
                      },
                      {
                        label: DSCR_STRUCTURAL_BREACH_LABEL,
                        pct: mcResult.breach.dscrBreachPct,
                      },
                    ].map(({ label, pct }) => {
                      const color =
                        pct > 0.3
                          ? "hsl(var(--c-destructive))"
                          : pct > 0.1
                            ? "hsl(var(--c-warning))"
                            : "hsl(var(--c-success))";
                      return (
                        <div
                          key={label}
                          className="row between"
                          style={{ gap: 12, alignItems: "center" }}
                        >
                          <span className="p-small">{label}</span>
                          <span
                            className="mono"
                            style={{
                              color,
                              fontWeight: 600,
                              minWidth: 48,
                              textAlign: "right",
                            }}
                          >
                            {Fmt.pct(pct, 0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: "1px solid hsl(var(--c-muted))",
                    }}
                  >
                    <span
                      className="p-micro"
                      style={{
                        color: "hsl(var(--c-foreground-muted))",
                        fontStyle: "italic",
                      }}
                    >
                      Stochastic simulation — not a forecast. Probabilities
                      reflect uniform-random variation across selected drivers.
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
